import { useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2, Info } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { api, errMsg, fmtEur, fmtDate, BILLINGS, COMMITMENTS, PERIOD_MONTHS } from "@/lib/api";

const BILLING_OPTS = Object.entries(BILLINGS).filter(([k]) => k !== "una_tantum");
const COMMIT_OPTS = Object.entries(COMMITMENTS);

const withBase = (r) => ({ ...r, data: { ...r.data, base_unit_price: r.data.unit_price, base_period: r.data.commitment || r.data.billing || "mensile" } });
const rescale = (d, commitment) => {
  const unit_price = Math.round(d.base_unit_price * (PERIOD_MONTHS[commitment] || 1) / (PERIOD_MONTHS[d.base_period] || 1) * 100) / 100;
  return { ...d, commitment, unit_price, price: Math.round(unit_price * d.quantity * 100) / 100 };
};
const applyPatch = (d, patch) => (patch.commitment !== undefined ? rescale({ ...d, ...patch }, patch.commitment) : { ...d, ...patch });

const Native = ({ value, onChange, options, placeholder, testid, className = "" }) => (
  <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
    className={`h-8 rounded-md border border-slate-700 bg-[#0F172A] px-2 text-xs text-slate-100 focus:outline-none focus:ring-1 focus:ring-blue-500 ${className}`}>
    {placeholder && <option value="">{placeholder}</option>}
    {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

function RowsTable({ rows, setRow }) {
  return (
    <div className="max-h-[42vh] overflow-auto rounded-md border border-slate-800">
      <Table>
        <TableHeader>
          <TableRow className="border-slate-800 hover:bg-transparent">
            <TableHead className="w-10">#</TableHead><TableHead>Nome servizio</TableHead>
            <TableHead className="text-right">Licenze</TableHead><TableHead className="text-right">P. unitario</TableHead>
            <TableHead>Inizio</TableHead><TableHead>Rinnovo Servizio</TableHead>
            <TableHead>Fatturazione</TableHead><TableHead>Obbligo</TableHead><TableHead>Esito</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={r.row} className={`border-slate-800 ${r.errors.length ? "bg-rose-500/5" : ""}`} data-testid={`xls-row-${r.row}`}>
              <TableCell className="font-mono text-slate-500">{r.row}</TableCell>
              <TableCell className="font-medium">{r.data.service_name || <span className="text-slate-600">—</span>}</TableCell>
              <TableCell className="text-right font-mono">{r.data.quantity}</TableCell>
              <TableCell className="text-right whitespace-nowrap">
                <div className="font-mono" data-testid={`xls-unit-${r.row}`}>{fmtEur(r.data.unit_price)}</div>
                {r.data.commitment && r.data.commitment !== r.data.base_period && (
                  <div className="text-[10px] text-amber-300" data-testid={`xls-unit-scaled-${r.row}`}>da {fmtEur(r.data.base_unit_price)} {COMMITMENTS[r.data.base_period]?.toLowerCase()}</div>
                )}
              </TableCell>
              <TableCell className="text-slate-400 whitespace-nowrap">{fmtDate(r.data.start_date)}</TableCell>
              <TableCell className="text-slate-400 whitespace-nowrap">{fmtDate(r.data.renewal_date)}</TableCell>
              <TableCell><Native value={r.data.billing} onChange={(v) => setRow(i, { billing: v })} options={BILLING_OPTS} placeholder="—" testid={`xls-billing-${r.row}`} /></TableCell>
              <TableCell><Native value={r.data.commitment} onChange={(v) => setRow(i, { commitment: v })} options={COMMIT_OPTS} placeholder="—" testid={`xls-commitment-${r.row}`} /></TableCell>
              <TableCell className="text-xs">
                {r.errors.length
                  ? <span className="flex items-start gap-1 text-rose-400"><AlertTriangle size={13} className="mt-0.5 shrink-0" />{r.errors.join("; ")}</span>
                  : r.warnings.length
                    ? <span className="flex items-start gap-1 text-amber-300"><Info size={13} className="mt-0.5 shrink-0" />{r.warnings.join("; ")}</span>
                    : <span className="flex items-center gap-1 text-emerald-400"><CheckCircle2 size={13} />OK</span>}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function ImportExcelDialog({ clientId, pages, onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [replaceMode, setReplaceMode] = useState(true);
  const [confirmReplace, setConfirmReplace] = useState(false);
  const subPages = pages.filter((p) => p.show_subscriptions);
  const [pageId, setPageId] = useState(() => (subPages.find((p) => /365|microsoft/i.test(p.title)) || subPages[0])?.id || "");

  const analyze = async () => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/clients/${clientId}/subscriptions/import-excel`, fd);
      setResult({ ...data, rows: data.rows.map(withBase) });
      setReplaceMode(data.meta.vat_match !== false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const setRow = (i, patch) => setResult((r) => ({ ...r, rows: r.rows.map((x, j) => (j === i ? { ...x, data: applyPatch(x.data, patch) } : x)) }));
  const setAll = (patch) => setResult((r) => ({ ...r, rows: r.rows.map((x) => ({ ...x, data: applyPatch(x.data, patch) })) }));

  const ready = result?.rows.filter((r) => !r.errors.length) || [];
  const incomplete = ready.filter((r) => !r.data.billing || !r.data.commitment).length;
  const existingOnPage = result?.meta.existing_by_page?.[pageId] || 0;

  const confirm = async () => {
    const willDelete = replaceMode ? existingOnPage : 0;
    if (willDelete && !confirmReplace) return setConfirmReplace(true);
    setBusy(true);
    try {
      const rows = ready.map(({ data: { base_unit_price, base_period, ...d } }) => ({ ...d, client_id: clientId, page_id: pageId }));
      const { data } = await api.post(`/admin/clients/${clientId}/subscriptions/bulk`, { page_id: pageId, rows, replace_page: replaceMode });
      toast.success(data.deleted ? `${data.deleted} abbonamenti sostituiti con ${data.imported} importati` : `${data.imported} abbonamenti importati`);
      onImported();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const m = result?.meta;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl bg-[#111827] border-slate-800" data-testid="import-excel-dialog">
        <DialogHeader>
          <DialogTitle>Importa da Excel del fornitore (Microsoft CSP)</DialogTitle>
          <DialogDescription className="text-slate-400">
            Carica il file <span className="font-mono">Subscriptions_*.xlsx</span>: Nome personalizzato sottoscrizione → Nome servizio, Quantità → Licenze, Listino → Prezzo unitario,
            Data inizio → Data inizio, Data scadenza → Rinnovo Servizio. Poi scegli Fatturazione e Obbligo contrattuale prima di confermare:
            il Listino del file è riferito al "Termine" del fornitore, quindi cambiando l'Obbligo il prezzo unitario viene riproporzionato (es. mensile → annuale ×12).
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="flex flex-wrap items-center gap-3">
            <label className="flex-1 min-w-[260px] flex items-center gap-3 rounded-md border border-dashed border-slate-700 bg-slate-900/40 px-4 py-3 cursor-pointer hover:border-blue-500/50 transition-colors">
              <FileSpreadsheet size={20} className="text-emerald-400 shrink-0" />
              <span className="text-sm text-slate-300 truncate" data-testid="xls-file-name">{file ? file.name : "Scegli il file .xlsx"}</span>
              <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="xls-file-input" />
            </label>
            <Button onClick={analyze} disabled={!file || busy} className="bg-blue-600 hover:bg-blue-500" data-testid="xls-analyze-button">
              {busy ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : null}Leggi file
            </Button>
          </div>
        )}

        {result && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm rounded-md border border-slate-800 bg-slate-900/40 px-4 py-3" data-testid="xls-meta">
              <span className="text-slate-400">Nel file: <strong className="text-slate-100">{m.company || "—"}</strong>{m.vat && <span className="text-slate-500"> · P.IVA {m.vat}</span>}</span>
              <span className="text-slate-400">Destinazione: <strong className="text-slate-100">{m.client_company}</strong></span>
              {m.vat_match === false && <span className="flex items-center gap-1 text-amber-300" data-testid="xls-vat-warning"><AlertTriangle size={14} />La P.IVA del file non coincide con quella del cliente</span>}
              <span className="ml-auto text-emerald-400"><strong data-testid="xls-valid-count">{result.valid_count}</strong> righe valide</span>
              {result.error_count > 0 && <span className="text-rose-400"><strong>{result.error_count}</strong> con errori (ignorate)</span>}
            </div>

            <div className="grid sm:grid-cols-3 gap-3 rounded-md border border-blue-500/20 bg-blue-500/5 px-4 py-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Pagina / tipologia di destinazione</Label>
                <Native value={pageId} onChange={setPageId} options={subPages.map((p) => [p.id, p.title])} testid="xls-page-select" className="w-full h-9 text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Fatturazione — applica a tutte le righe</Label>
                <Native value="" onChange={(v) => v && setAll({ billing: v })} options={BILLING_OPTS} placeholder={m.has_billing ? "Come nel file (Tipo fatturazione)" : "Scegli…"} testid="xls-billing-all" className="w-full h-9 text-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Obbligo contrattuale — applica a tutte le righe</Label>
                <Native value="" onChange={(v) => v && setAll({ commitment: v })} options={COMMIT_OPTS} placeholder={m.has_commitment ? "Come nel file (Termine)" : "Scegli…"} testid="xls-commitment-all" className="w-full h-9 text-sm" />
              </div>
            </div>

            <RowsTable rows={result.rows} setRow={setRow} />
            {incomplete > 0 && <p className="text-xs text-amber-300" data-testid="xls-incomplete">{incomplete} righe senza Fatturazione o Obbligo: completale (per riga o con "applica a tutte") per poter importare.</p>}

            {existingOnPage > 0 && (
              <div className={`rounded-md border px-4 py-3 text-sm ${confirmReplace ? "border-rose-500/50 bg-rose-500/10" : "border-amber-500/30 bg-amber-500/5"}`} data-testid="xls-replace-box">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={replaceMode} onChange={(e) => { setReplaceMode(e.target.checked); setConfirmReplace(false); }} className="mt-0.5 h-4 w-4 accent-amber-400" data-testid="xls-replace-checkbox" />
                  <span>
                    <span className="flex items-center gap-1.5 text-amber-300 font-medium"><AlertTriangle size={14} />Sostituisci gli abbonamenti esistenti della pagina "{subPages.find((p) => p.id === pageId)?.title}"</span>
                    <span className="block text-slate-400 mt-0.5">Il cliente ha già <strong className="text-slate-100" data-testid="xls-existing-count">{existingOnPage}</strong> abbonamenti in questa pagina: verranno <strong className="text-rose-300">eliminati</strong> e rimpiazzati dalle {ready.length} righe del file. Deseleziona per aggiungerli invece ai presenti.</span>
                    {confirmReplace && replaceMode && <span className="block mt-2 text-rose-300 font-medium" data-testid="xls-replace-confirm-msg">Confermi? Premi di nuovo "Sostituisci e importa" per procedere: l'eliminazione non è reversibile.</span>}
                  </span>
                </label>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="xls-cancel-button">Annulla</Button>
          {result && <Button variant="outline" onClick={() => { setResult(null); setFile(null); }} className="border-slate-700 bg-transparent" data-testid="xls-reset-button">Altro file</Button>}
          <Button onClick={confirm} disabled={!result || !ready.length || incomplete > 0 || !pageId || busy} className={replaceMode && existingOnPage ? "bg-rose-600 hover:bg-rose-500" : "bg-emerald-600 hover:bg-emerald-500"} data-testid="xls-confirm-button">
            {busy && result ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : null}
            {replaceMode && existingOnPage ? `Sostituisci ${existingOnPage} e importa ${ready.length}` : `Importa ${ready.length} abbonamenti`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
